
import {
    IContextualMenuItem,
    Separator, Stack,
    Text,
    useTheme
} from "@fluentui/react";
import { AddIcon } from '@fluentui/react-icons-mdl2';
import { RootState } from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import LinkMenu from "components/controls/LinkMenu";
import SimpleCard from "components/controls/SimpleCard";
import { AppLinkTo, EViewType } from "global/Application";
import { useApplication } from "global/Application/Context";
import { updateWorkspace } from "global/Reducer/statusSlice";
import React, { useMemo } from "react";

import {
    IBenefit as FAM_IBenefit,
    IPlan as FAM_IPlan
} from "model/FlexibleAttributeModel";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import PlanCard from "views/BlueRelationship/PlanCard";
import PlanGrid from "../PlanGrid";

interface IPlanPanelProps {
    onAdd?: () => void;
    plans?: FAM_IPlan[];
    plansBenefit?: FAM_IBenefit;
}


const PlanPanel: React.FunctionComponent<IPlanPanelProps> = (props) => {
    const application = useApplication();
    const dispatch = useDispatch();
    const theme = useTheme();
    const state = useSelector((state: RootState) => state);
    const history = useHistory();

    const workspace = state.status.workspace;
    const disabled = state.status.viewType !== EViewType.Modify;

    const plans: FAM_IPlan[] = [];

    if (props.plansBenefit) {
        state.data.json.plans.forEach(ePlan => {
            if (ePlan.benefitRefs.find(q => q.id === props.plansBenefit?.id))
                plans.push(ePlan);
        });
    } else if (props.plans)
        plans.push(...props.plans);
    else
        plans.push(...state.data.json.plans);

    if (workspace.plan === "cardSorted")
        plans.sort((a, b) => a.name?.localeCompare(b.name!));

    const menuItemsPlan = useMemo(() => {
        const menuItems: IContextualMenuItem[] = [
            {
                key: 'card',
                text: 'Card View - Sorted By Entry Sequence',
            },
            {
                key: 'cardSorted',
                text: 'Card View - Sorted By Name',
            },
            {
                key: 'grid',
                text: 'Grid View',
            },
        ];
        return menuItems;
    }, []);

    function onRenderPlan() {
        const items: IContextualMenuItem[] = [];

        if (props.onAdd)
            if (!disabled && application.ChangeTypePermissions.benefit.add)
                items.push({
                    key: 'groupConfiguration.plan.add',
                    name: 'Add',
                    icon: 'DocumentManagement',
                    onClick: (event, item) => {
                        event?.preventDefault();
                        event?.stopPropagation();
                        props.onAdd?.();
                    },
                },);

        return <CustomPanel title={"Plans"}
            canCollapse={true}
            defaultCollapsed={true}
            contentStyle={{ overflow: 'visible' }}
            storageId={"groupConfiguration.plans"}
            items={items}
            mountWhenExpanded={true}
        >
            <LinkMenu
                styles={{ root: { marginTop: '12px' } }}
                menuItems={menuItemsPlan}
                selectedKey={workspace.plan}
                onChange={(event, menuItem) => {
                    dispatch(updateWorkspace({ plan: menuItem.key }));
                }}
            />
            <Separator />
            {workspace.plan === "grid" && onRenderPlanGrid()}
            {workspace.plan === "card" && onRenderPlanCard()}
            {workspace.plan === "cardSorted" && onRenderPlanCard()}
        </CustomPanel>;
    }

    function onRenderPlanCard() {
        return <Stack
            horizontal
            wrap
            verticalFill
            tokens={{ childrenGap: 15 }}
            horizontalAlign="start"
            style={{ padding: '15px', marginBottom: '15px' }}
        >
            {plans.map(ePlan => {

                // skip deactivated records/only show deactivated records if permitted at group config properties level
                if ((ePlan.deactivated ?? false) !== state.data.json.showInActives?.value)
                    return <React.Fragment key={ePlan.id}></React.Fragment>;

                return (
                    <PlanCard
                        key={ePlan.id}
                        plan={ePlan}
                        onClick={() => history.push(AppLinkTo(state, { id: ePlan.id, type: 'plan' }))}
                        state={state}
                    />
                );
            })}


            {!disabled && application.ChangeTypePermissions.plan.add && props.onAdd &&
                <SimpleCard
                    key={"defaultPlan"}
                    onClick={props.onAdd}
                    onRenderFront={() => {
                        return (
                            <>
                                <p><Text>Add</Text></p>
                                <AddIcon style={{ fontSize: 'x-large', color: theme.palette.themePrimary }} />
                            </>
                        );
                    }}
                />
            }
        </Stack>;
    }

    function onRenderPlanGrid() {
        return <PlanGrid plans={plans} />
    }

    return onRenderPlan();
}

export default PlanPanel;